import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Input,
  Text,
  Box,
  FormControl,
  FormLabel,
  Switch,
  Grid,
  GridItem,
  IconButton,
  Badge,
  Flex,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  NumberInput,
  NumberInputField,
  useToast,
} from '@chakra-ui/react';

import { FaPlus, FaTrash, FaEdit, FaEye, FaServer } from 'react-icons/fa';

import { useColors } from './colors';
import ConfirmationPopup from 'components/popup/ConfirmationPopup';

import {
  getSftpConnectionsApi,
  getSftpConnectionApi,
  createSftpConnectionApi,
  updateSftpConnectionApi,
  deleteSftpConnectionApi,
} from 'features/suppliers/suppliers.api';

const SftpSettingsModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const {
    textColor,
    inputColor,
    backgroundCardColor,
    borderColor,
    secondaryTextColor,
  } = useColors();

  const {
    sftpConnections,
    getSftpConnectionsStatus,
    createSftpConnectionStatus,
    updateSftpConnectionStatus,
    deleteSftpConnectionStatus,
  } = useSelector((state) => state.suppliers);

  const [view, setView] = useState('list'); // 'list' | 'form' | 'detail'
  const [editingId, setEditingId] = useState(null);
  const [detailConnection, setDetailConnection] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [form, setForm] = useState({
    name: '',
    host: '',
    port: 22,
    username: '',
    password: '',
    remoteBasePath: '/',
  });

  useEffect(() => {
    if (isOpen) {
      setView('list');
      setEditingId(null);
      setDetailConnection(null);
      setForm({
        name: '',
        host: '',
        port: 22,
        username: '',
        password: '',
        remoteBasePath: '/',
      });
      dispatch(getSftpConnectionsApi(false));
    }
  }, [isOpen, dispatch]);

  const handleAddNew = () => {
    setForm({
      name: '',
      host: '',
      port: 22,
      username: '',
      password: '',
      remoteBasePath: '/',
    });
    setEditingId(null);
    setView('form');
  };

  const handleEdit = (conn) => {
    setForm({
      name: conn.name || '',
      host: conn.host || '',
      port: conn.port ?? 22,
      username: conn.username || '',
      password: '',
      remoteBasePath: conn.remoteBasePath || '/',
    });
    setEditingId(conn._id);
    setView('form');
  };

  const handleViewDetail = async (conn) => {
    setDetailConnection(conn);
    setView('detail');
  };

  const handleBackToList = () => {
    setView('list');
    setEditingId(null);
    setDetailConnection(null);
    dispatch(getSftpConnectionsApi(false));
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmitForm = () => {
    if (!form.name?.trim() || !form.host?.trim() || !form.username?.trim()) {
      toast({
        title: 'Validation',
        description: 'Name, host and username are required.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (!editingId && !form.password?.trim()) {
      toast({
        title: 'Validation',
        description: 'Password is required when creating a new connection.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const port = parseInt(form.port, 10) || 22;
    if (editingId) {
      const payload = {
        name: form.name.trim(),
        host: form.host.trim(),
        port,
        username: form.username.trim(),
        remoteBasePath: (form.remoteBasePath || '/').trim() || '/',
      };
      if (form.password?.trim()) payload.password = form.password;
      dispatch(updateSftpConnectionApi({ id: editingId, data: payload })).then(
        (res) => {
          if (updateSftpConnectionApi.fulfilled.match(res)) {
            handleBackToList();
          }
        },
      );
    } else {
      dispatch(
        createSftpConnectionApi({
          name: form.name.trim(),
          host: form.host.trim(),
          port,
          username: form.username.trim(),
          password: form.password,
          remoteBasePath: (form.remoteBasePath || '/').trim() || '/',
        }),
      ).then((res) => {
        if (createSftpConnectionApi.fulfilled.match(res)) {
          handleBackToList();
        }
      });
    }
  };

  const handleToggleActive = (conn) => {
    dispatch(
      updateSftpConnectionApi({
        id: conn._id,
        data: { isActive: !conn.isActive },
      }),
    ).then((res) => {
      if (updateSftpConnectionApi.fulfilled.match(res)) {
        dispatch(getSftpConnectionsApi(false));
      }
    });
  };

  const handleDeleteClick = (conn) => setDeleteTarget(conn);
  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    dispatch(deleteSftpConnectionApi(deleteTarget._id)).then((res) => {
      if (deleteSftpConnectionApi.fulfilled.match(res)) {
        setDeleteTarget(null);
        handleBackToList();
      }
    });
  };

  const isFormLoading =
    createSftpConnectionStatus === 'pending' ||
    updateSftpConnectionStatus === 'pending';

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent
          maxW="900px"
          maxH="90vh"
          borderRadius="xl"
          boxShadow="2xl"
          bg={backgroundCardColor}
        >
          <ModalHeader
            color={textColor}
            fontSize="xl"
            fontWeight="600"
            pb={2}
            borderBottom="1px solid"
            borderColor={borderColor}
          >
            <HStack spacing={3}>
              <Box color="blue.500">
                <FaServer size={24} />
              </Box>
              <Box>
                <Text>SFTP Settings</Text>
                <Text fontSize="sm" fontWeight="normal" color={secondaryTextColor}>
                  Manage SFTP connections for inventory file fetching
                </Text>
              </Box>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} overflowY="auto" maxH="calc(90vh - 160px)">
            {view === 'list' && (
              <VStack align="stretch" spacing={6}>
                <Flex justify="space-between" align="center">
                  <Text fontSize="md" color={secondaryTextColor}>
                    {sftpConnections?.length ?? 0} connection(s)
                  </Text>
                  <Button
                    leftIcon={<FaPlus />}
                    size="md"
                    colorScheme="blue"
                    borderRadius="md"
                    onClick={handleAddNew}
                    fontWeight="500"
                  >
                    Add SFTP Connection
                  </Button>
                </Flex>

                {getSftpConnectionsStatus === 'pending' ? (
                  <Text color={secondaryTextColor}>Loading...</Text>
                ) : !sftpConnections?.length ? (
                  <Box
                    p={8}
                    borderRadius="lg"
                    border="2px dashed"
                    borderColor={borderColor}
                    textAlign="center"
                  >
                    <Text color={secondaryTextColor} mb={2}>
                      No SFTP connections yet
                    </Text>
                    <Text fontSize="sm" color={secondaryTextColor} mb={4}>
                      Add a connection to link suppliers and fetch inventory files
                      automatically.
                    </Text>
                    <Button
                      colorScheme="blue"
                      variant="outline"
                      leftIcon={<FaPlus />}
                      onClick={handleAddNew}
                    >
                      Add your first connection
                    </Button>
                  </Box>
                ) : (
                  <Box
                    border="1px solid"
                    borderColor={borderColor}
                    borderRadius="lg"
                    overflow="hidden"
                  >
                    <Table size="sm" variant="simple">
                      <Thead bg="gray.50" _dark={{ bg: 'whiteAlpha.50' }}>
                        <Tr>
                          <Th color={textColor}>Name</Th>
                          <Th color={textColor}>Host</Th>
                          <Th color={textColor}>Port</Th>
                          <Th color={textColor}>Status</Th>
                          <Th color={textColor} textAlign="right">
                            Actions
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {sftpConnections.map((conn) => (
                          <Tr
                            key={conn._id}
                            _hover={{ bg: 'gray.50', _dark: { bg: 'whiteAlpha.50' } }}
                          >
                            <Td color={textColor} fontWeight="500">
                              {conn.name}
                            </Td>
                            <Td color={textColor}>{conn.host}</Td>
                            <Td color={textColor}>{conn.port ?? 22}</Td>
                            <Td>
                              <Badge
                                colorScheme={conn.isActive ? 'green' : 'gray'}
                                variant="subtle"
                                borderRadius="md"
                                px={2}
                                py={1}
                              >
                                {conn.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </Td>
                            <Td textAlign="right">
                              <HStack spacing={1} justify="flex-end">
                                <IconButton
                                  icon={<FaEye />}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="blue"
                                  aria-label="View details"
                                  onClick={() => handleViewDetail(conn)}
                                />
                                <IconButton
                                  icon={<FaEdit />}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="blue"
                                  aria-label="Edit"
                                  onClick={() => handleEdit(conn)}
                                />
                                <Switch
                                  size="sm"
                                  isChecked={!!conn.isActive}
                                  onChange={() => handleToggleActive(conn)}
                                  colorScheme="blue"
                                />
                                <IconButton
                                  icon={<FaTrash />}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="red"
                                  aria-label="Delete"
                                  onClick={() => handleDeleteClick(conn)}
                                />
                              </HStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </VStack>
            )}

            {view === 'form' && (
              <VStack align="stretch" spacing={6}>
                <Text fontSize="lg" fontWeight="600" color={textColor}>
                  {editingId ? 'Edit SFTP Connection' : 'Add SFTP Connection'}
                </Text>
                <Grid templateColumns="1fr 1fr" gap={4}>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel color={textColor} fontSize="sm">
                        Connection name
                      </FormLabel>
                      <Input
                        value={form.name}
                        onChange={(e) => handleFormChange('name', e.target.value)}
                        placeholder="e.g. ACME Supplier SFTP"
                        color={inputColor}
                        borderColor={borderColor}
                        _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel color={textColor} fontSize="sm">
                        Host
                      </FormLabel>
                      <Input
                        value={form.host}
                        onChange={(e) => handleFormChange('host', e.target.value)}
                        placeholder="sftp.example.com"
                        color={inputColor}
                        borderColor={borderColor}
                        _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel color={textColor} fontSize="sm">
                        Port
                      </FormLabel>
                      <NumberInput
                        value={form.port}
                        onChange={(_, val) => handleFormChange('port', val)}
                        min={1}
                        max={65535}
                      >
                        <NumberInputField
                          color={inputColor}
                          borderColor={borderColor}
                          _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
                        />
                      </NumberInput>
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl isRequired>
                      <FormLabel color={textColor} fontSize="sm">
                        Username
                      </FormLabel>
                      <Input
                        value={form.username}
                        onChange={(e) => handleFormChange('username', e.target.value)}
                        placeholder="sftp_user"
                        color={inputColor}
                        borderColor={borderColor}
                        _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={2}>
                    <FormControl>
                      <FormLabel color={textColor} fontSize="sm">
                        Remote directory path
                      </FormLabel>
                      <Input
                        value={form.remoteBasePath}
                        onChange={(e) => handleFormChange('remoteBasePath', e.target.value)}
                        placeholder="/ or /upload/inventory"
                        color={inputColor}
                        borderColor={borderColor}
                        _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
                      />
                      <Text fontSize="xs" color={secondaryTextColor} mt={1}>
                        Folder on the SFTP server where inventory files are listed and downloaded.
                      </Text>
                    </FormControl>
                  </GridItem>
                  <GridItem colSpan={2}>
                    <FormControl isRequired={!editingId}>
                      <FormLabel color={textColor} fontSize="sm">
                        Password {editingId && '(leave blank to keep current)'}
                      </FormLabel>
                      <Input
                        type="password"
                        value={form.password}
                        onChange={(e) => handleFormChange('password', e.target.value)}
                        placeholder={editingId ? '••••••••' : 'Enter password'}
                        color={inputColor}
                        borderColor={borderColor}
                        _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px blue.400' }}
                      />
                    </FormControl>
                  </GridItem>
                </Grid>
                <Flex justify="flex-end" gap={3}>
                  <Button variant="ghost" onClick={handleBackToList}>
                    Cancel
                  </Button>
                  <Button
                    colorScheme="blue"
                    onClick={handleSubmitForm}
                    isLoading={isFormLoading}
                    loadingText="Saving..."
                  >
                    {editingId ? 'Update' : 'Create'}
                  </Button>
                </Flex>
              </VStack>
            )}

            {view === 'detail' && detailConnection && (
              <VStack align="stretch" spacing={4}>
                <Text fontSize="lg" fontWeight="600" color={textColor}>
                  Connection details
                </Text>
                <Box
                  p={5}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={borderColor}
                  bg="gray.50"
                  _dark={{ bg: 'whiteAlpha.50' }}
                >
                  <VStack align="stretch" spacing={3}>
                    <Flex justify="space-between">
                      <Text color={secondaryTextColor} fontSize="sm">
                        Name
                      </Text>
                      <Text color={textColor} fontWeight="500">
                        {detailConnection.name}
                      </Text>
                    </Flex>
                    <Divider />
                    <Flex justify="space-between">
                      <Text color={secondaryTextColor} fontSize="sm">
                        Host
                      </Text>
                      <Text color={textColor}>{detailConnection.host}</Text>
                    </Flex>
                    <Divider />
                    <Flex justify="space-between">
                      <Text color={secondaryTextColor} fontSize="sm">
                        Port
                      </Text>
                      <Text color={textColor}>{detailConnection.port ?? 22}</Text>
                    </Flex>
                    <Divider />
                    <Flex justify="space-between">
                      <Text color={secondaryTextColor} fontSize="sm">
                        Username
                      </Text>
                      <Text color={textColor}>{detailConnection.username}</Text>
                    </Flex>
                    <Divider />
                    <Flex justify="space-between">
                      <Text color={secondaryTextColor} fontSize="sm">
                        Remote path
                      </Text>
                      <Text color={textColor}>{detailConnection.remoteBasePath || '/'}</Text>
                    </Flex>
                    <Divider />
                    <Flex justify="space-between">
                      <Text color={secondaryTextColor} fontSize="sm">
                        Password
                      </Text>
                      <Text color={textColor}>
                        {detailConnection.passwordMasked || '••••••••'}
                      </Text>
                    </Flex>
                    <Divider />
                    <Flex justify="space-between">
                      <Text color={secondaryTextColor} fontSize="sm">
                        Status
                      </Text>
                      <Badge
                        colorScheme={detailConnection.isActive ? 'green' : 'gray'}
                        variant="subtle"
                      >
                        {detailConnection.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </Flex>
                  </VStack>
                </Box>
                <Flex justify="flex-end" gap={3}>
                  <Button variant="ghost" onClick={handleBackToList}>
                    Back to list
                  </Button>
                  <Button
                    colorScheme="blue"
                    leftIcon={<FaEdit />}
                    onClick={() => handleEdit(detailConnection)}
                  >
                    Edit
                  </Button>
                </Flex>
              </VStack>
            )}
          </ModalBody>
          {view === 'list' && (
            <ModalFooter
              pt={4}
              borderTop="1px solid"
              borderColor={borderColor}
            >
              <Button variant="ghost" mr={3} onClick={onClose} fontWeight="500">
                Close
              </Button>
            </ModalFooter>
          )}
        </ModalContent>
      </Modal>

      <ConfirmationPopup
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete SFTP connection?"
        description={
          deleteTarget
            ? `"${deleteTarget.name}" will be removed. Suppliers using this connection will need to be updated.`
            : ''
        }
        confirmButtonText="Delete"
      />
    </>
  );
};

export default SftpSettingsModal;
